import { date } from '@seek/ca-myprofile-utils';
import type { Language, Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Inline,
  Strong,
  Text,
  useResponsiveValue,
} from 'braid-design-system';
import {
  type ReactNode,
  useEffect,
  useState,
  type ComponentProps,
  type FC,
} from 'react';

import type { ReferenceCheckType } from 'src/App/ActionsPage/graphql/types.generated.ts';
import { ApplicationBadgesInOrder } from 'src/App/AppPageBlock/ApplicationBadgesInOrder';
import { HighFitBadgeWithTooltip } from 'src/App/AppPageBlock/HighFitBadge';
import type { JOB_APPLICATION_TAB } from 'src/constants/tabs';
import { Display } from 'src/libs/Display';
import { BUCKETS } from 'src/stores/JobApplicationStore/types';
import buildHandleEnterAndSpaceKeyPress from 'src/utils/buildHandleEnterAndSpaceKeyPress.ts';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';

import { useFeatureFlag } from '../FeatureFlagsContext.ts';

import translations from './.vocab/index.ts';
import { AppliedDate } from './AppliedDate/index.ts';
import { AttachmentsAndRecords } from './AttachmentsAndRecords/index.ts';
import { AvatarCheckbox } from './AvatarCheckbox/index.ts';
import { CertsyIdentityVerified } from './CertsyIdentityVerified/index.ts';
import { ChangeStatusButtons } from './ChangeStatusButtons/index.ts';
import { HighFitDisclosure } from './HighFitDisclosure/HighFitDisclosure.tsx';
import { JobApplicationCardLayout } from './JobApplicationCardLayout/index.ts';
import { MatchedQualities } from './MatchedQualities';
import { ProfileKeyInfo } from './ProfileKeyInfo/index.ts';
import { ReferenceCheckButton } from './ReferenceCheckButton/ReferenceCheckButton.tsx';
import { RoleRequirementsDisclosure } from './RoleRequirementsDisclosure/index.ts';
import { StarRatingButton } from './StarRatingButton.tsx/index.ts';
import { useHighFitDisclosure } from './useHighFitDisclosure.ts';
import { useRoleRequirementsDisclosure } from './useRoleRequirementsDisclosure';

import * as styles from './JobApplicationCard.css';

export interface JobApplicationCardProps {
  userId: number | null;
  seekAdvertiserId: string;
  jobId: string;
  applicationStatus: BUCKETS;
  currentBucket: BUCKETS;
  shouldShowNewBadge: boolean;
  shouldShowRelevantApplicationBadge: boolean;
  jobApplication: {
    id: string;
    applicationCorrelationId: string;
    candidateId: string;
    fullName: string;
    isUnread: boolean;
    appliedDate: string;
    source: number;
    rating: number;
    seekCandidateId: string;
    applicationId: string;
    isPriorityApply: boolean;
    highFitSummary: string | null;
  };
  lastRole?: {
    from: {
      month: number;
      year: number;
    };
    to?: {
      month: number;
      year: number;
    };
    title: string;
    company: string;
  };
  roleRequirements: ComponentProps<
    typeof RoleRequirementsDisclosure
  >['roleRequirements'];
  applicationListState: 'collapsed' | 'expanded';
  matchedQualities: {
    qualities: Array<{ id: string; displayLabel: string; isTop: boolean }>;
    hasError: boolean;
  };
  certsyVerifiedFullName?: string;
  education?: {
    name: string;
    institute: string;
    completionYear?: number;
  };
  homeLocation?: {
    description: string;
  };
  attachments: Array<{ attachmentId: string; type: number }>;
  emailCount: number;
  previousApplicationCount: number;
  notesAndAttachmentsCount: number;
  checkboxProps: {
    checked: boolean;
    onChange: () => void;
  };
  locale: Locale;
  nationalities: string[] | undefined;
  openDetailsDrawer: (
    tab: JOB_APPLICATION_TAB,
    applicationCardIsExpanded?: boolean,
  ) => void;
  openDetailsDrawerProfileTab: (applicationCardIsExpanded?: boolean) => void;
  markApplicationAsRead: () => void;
  changeStatus: (bucket: BUCKETS, isExpanded?: boolean) => Promise<void>;
  onClickRating: (index: number) => number;
  isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
  language: Language;
  homeLocationIdsForTracking: string[];
  nationalityIdsForTracking: string[];
  isFilterExpand: boolean;
  verificationsCount: number;
  verifications: string[];
  onVerificationsIconClick: () => void;
  referenceCheck:
    | {
        case: 'no-valid-budget';
        onReferenceCheckButtonClick: () => void | null;
      }
    | {
        case: 'reference-requested-or-available';
        referenceCheckType: ReferenceCheckType;
        counts?: { finishedCount: number; totalCount: number };
        onReferencesIconClick: () => void | null;
      }
    | {
        case: 'references-not-requested-and-budget-valid';
        onReferenceCheckButtonClick: () => void | null;
      };
  canPurchaseReferenceChecks: boolean;
  data?: ComponentProps<typeof Box>['data'];
}

export const JobApplicationCard: FC<JobApplicationCardProps> = ({
  jobId,
  applicationStatus,
  currentBucket,
  shouldShowNewBadge,
  shouldShowRelevantApplicationBadge,
  jobApplication,
  lastRole,
  matchedQualities,
  homeLocation,
  education,
  certsyVerifiedFullName,
  attachments,
  emailCount,
  previousApplicationCount,
  notesAndAttachmentsCount,
  verificationsCount,
  checkboxProps,
  locale,
  applicationListState,
  roleRequirements,
  verifications,
  openDetailsDrawer,
  openDetailsDrawerProfileTab,
  markApplicationAsRead,
  changeStatus,
  onClickRating,
  isVerifiedId,
  nationalities,
  language,
  homeLocationIdsForTracking,
  nationalityIdsForTracking,
  isFilterExpand,
  onVerificationsIconClick,
  referenceCheck,
  canPurchaseReferenceChecks,
  data,
}) => {
  const { t } = useTranslations(translations);

  const responsiveValue = useResponsiveValue();

  const isMobile =
    responsiveValue({
      mobile: true,
      tablet: false,
      desktop: false,
    }) ?? false;

  const showPriorityApply = jobApplication.isPriorityApply;

  const {
    isRoleRequirementsDisclosureExpanded,
    hasRoleRequirements,
    roleRequirementsDiscloserOnClick,
  } = useRoleRequirementsDisclosure({
    roleRequirementsLength: roleRequirements.length,
    applicationListState,
  });

  const { variant } = useFeatureFlag('cm_high_fit_applicant_explainability');
  const isHighFitExperimentEnabled = variant === 'challenger';

  const isHighFitCandidate = isHighFitCandidateCheck(
    jobApplication,
    isHighFitExperimentEnabled,
  );
  const { isHighFitDisclosureExpanded, highFitDisclosureOnClick } =
    useHighFitDisclosure({
      isHighFitCandidate,
      applicationListState,
    });

  return (
    <Box
      data-hj-masked
      tabIndex={0}
      position="relative"
      cursor="pointer"
      onKeyDown={buildHandleEnterAndSpaceKeyPress(openDetailsDrawerProfileTab)}
      onClick={() => openDetailsDrawerProfileTab()}
      data={data}
    >
      <JobApplicationCardLayout
        avatarCheckBox={
          <AvatarCheckbox
            jobApplication={jobApplication}
            checkboxProps={checkboxProps}
          />
        }
        fullNameAndCertifiedIcon={
          <Inline space="xxsmall" alignY="center">
            <Text weight="strong">{jobApplication.fullName}</Text>
            {certsyVerifiedFullName ? (
              <CertsyIdentityVerified
                idSuffix={jobApplication.id}
                certsyVerifiedFullName={certsyVerifiedFullName}
                boxProps={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              />
            ) : null}
          </Inline>
        }
        highlightTag={
          isHighFitCandidate ? (
            <Display desktop>
              <HighFitBadgeWithTooltip
                variant="tag"
                applicantName={jobApplication.fullName}
              />
            </Display>
          ) : null
        }
        recentRoleAndTenure={
          lastRole ? (
            <RecentRoleAndTenure lastRole={lastRole} locale={locale} />
          ) : (
            <Text size="small" weight="strong">
              {t('New to workforce')}
            </Text>
          )
        }
        keyInfo={
          <ProfileKeyInfo
            homeLocation={homeLocation}
            education={education}
            nationalities={nationalities}
            language={language}
          />
        }
        badges={
          <ApplicationBadgesInOrder
            shouldShowNewBadge={shouldShowNewBadge}
            shouldShowHighFitBadge={isHighFitCandidate}
            shouldShowRelevantApplicationBadge={
              shouldShowRelevantApplicationBadge
            }
            shouldShowPriorityApplyBadge={showPriorityApply}
            referenceCheck={
              (referenceCheck.case === 'reference-requested-or-available' &&
                (referenceCheck.counts !== undefined
                  ? {
                      referenceCheckType: referenceCheck.referenceCheckType,
                      finishedCount: referenceCheck.counts?.finishedCount,
                      status: 'available',
                    }
                  : {
                      status: 'requested',
                    })) ||
              undefined
            }
            verificationsCount={verificationsCount}
            verifications={verifications}
            jobApplicationId={jobApplication.id}
            applicationStatus={applicationStatus}
            applicationName={jobApplication.fullName}
            language={language}
          />
        }
        roleRequirementsDisclosure={
          hasRoleRequirements ? (
            <RoleRequirementsDisclosure
              roleRequirements={roleRequirements}
              jobApplicationId={jobApplication.id}
              isMobile={isMobile}
              isExpanded={isRoleRequirementsDisclosureExpanded}
              toggleIsExpanded={roleRequirementsDiscloserOnClick}
              isFilterExpand={isFilterExpand}
            />
          ) : null
        }
        highFitDisclosure={
          isHighFitCandidate ? (
            <HighFitDisclosure
              summary={jobApplication.highFitSummary}
              isExpanded={isHighFitDisclosureExpanded}
              onDisclosureClicked={highFitDisclosureOnClick}
              id={jobApplication.id}
            />
          ) : null
        }
        matchedSkillsDisclosure={
          !matchedQualities.hasError &&
          matchedQualities.qualities.length > 0 &&
          applicationListState === 'expanded' ? (
            <MatchedQualities
              candidateFullname={jobApplication.fullName}
              qualities={matchedQualities.qualities}
              jobApplicationId={jobApplication.id}
              limit={5}
            />
          ) : null
        }
        appliedDate={
          <AppliedDate
            appliedDate={jobApplication.appliedDate}
            source={jobApplication.source}
            locale={locale}
          />
        }
        attachmentsAndRecords={
          attachments ||
          emailCount ||
          previousApplicationCount ||
          notesAndAttachmentsCount ? (
            <AttachmentsAndRecords
              jobApplication={jobApplication}
              isVerifiedId={isVerifiedId}
              isMobile={isMobile}
              attachments={attachments}
              emailCount={emailCount}
              previousApplicationCount={previousApplicationCount}
              notesAndAttachmentsCount={notesAndAttachmentsCount}
              openDetailsDrawer={openDetailsDrawer}
              markApplicationAsRead={markApplicationAsRead}
              homeLocationIdsForTracking={homeLocationIdsForTracking}
              nationalityIdsForTracking={nationalityIdsForTracking}
              jobId={jobId}
              referenceCheckCounts={
                referenceCheck.case === 'reference-requested-or-available'
                  ? referenceCheck.counts
                  : undefined
              }
              onReferencesIconClick={() => {
                if (
                  referenceCheck.case === 'reference-requested-or-available'
                ) {
                  referenceCheck.onReferencesIconClick?.();
                }
              }}
              verificationsCount={verificationsCount}
              onVerificationsIconClick={onVerificationsIconClick}
            />
          ) : null
        }
        actionButtons={
          <Inline space="xsmall" alignY="center" align="right">
            <StarRatingButton
              onClickRating={onClickRating}
              rating={jobApplication.rating}
            />

            {currentBucket !== BUCKETS.INBOX &&
            currentBucket !== BUCKETS.NOT_SUITABLE &&
            (referenceCheck.case ===
              'references-not-requested-and-budget-valid' ||
              (referenceCheck.case === 'no-valid-budget' &&
                canPurchaseReferenceChecks)) ? (
              <ReferenceCheckButton
                onReferenceCheckButtonClick={
                  referenceCheck.onReferenceCheckButtonClick
                }
                applicationCorrelationId={
                  jobApplication.applicationCorrelationId
                }
                isFilterExpand={isFilterExpand}
              />
            ) : null}

            <ChangeStatusButtons
              jobApplication={jobApplication}
              currentBucket={currentBucket}
              changeStatus={changeStatus}
              isFilterExpand={isFilterExpand}
            />
          </Inline>
        }
      />
      {shouldShowNewBadge ? <Box className={styles.unreadLeftBorder} /> : null}
    </Box>
  );
};

const RecentRoleAndTenure: FC<{
  lastRole: NonNullable<JobApplicationCardProps['lastRole']>;
  locale: JobApplicationCardProps['locale'];
}> = ({ lastRole, locale }) => {
  const [timeSpan, setTimeSpan] = useState('');

  const { t } = useTranslations(translations);
  useEffect(() => {
    date
      .getTimeSpan(
        lastRole.from,
        lastRole.to,
        locale as Parameters<typeof date.getTimeSpan>[2],
      )
      .then((result) => setTimeSpan(result));
  }, [lastRole.from, lastRole.to, locale]);

  const recentRole = (): ReactNode => {
    if (!lastRole.company) {
      return <Strong>{lastRole.title}</Strong>;
    }

    return t('mostRecentJobTitle at mostRecentCompanyName', {
      Strong: (children) => <Strong>{children}</Strong>,
      mostRecentJobTitle: lastRole.title,
      mostRecentCompanyName: lastRole.company,
    });
  };

  return (
    <Inline space="xxsmall" alignY="center">
      <Text size="small">{recentRole()}</Text>
      <Text size="small">({timeSpan})</Text>
    </Inline>
  );
};

function isHighFitCandidateCheck(
  jobApplication: JobApplicationCardProps['jobApplication'],
  isHighFitExperimentEnabled: boolean,
): jobApplication is JobApplicationCardProps['jobApplication'] & {
  highFitSummary: string;
} {
  return Boolean(isHighFitExperimentEnabled && jobApplication.highFitSummary);
}
